import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { PartyPopper } from 'lucide-react'
import { invitation } from '@/constants/invitation'

type Phase = 'before' | 'during' | 'after'

type Remaining = {
    phase: Phase
    days: number
    hours: number
    minutes: number
    seconds: number
}

type Unit = 'days' | 'hours' | 'minutes' | 'seconds'

const START = new Date(invitation.startsAt).getTime()
const END = new Date(invitation.endsAt).getTime()

const UNITS: Array<{ key: Unit, singular: string, plural: string }> = [
    { key: 'days', singular: "jour", plural: "jours" },
    { key: 'hours', singular: "heure", plural: "heures" },
    { key: 'minutes', singular: "minute", plural: "minutes" },
    { key: 'seconds', singular: "seconde", plural: "secondes" }
]

const computeRemaining = (now: number): Remaining => {
    if (now >= END) return { phase: 'after', days: 0, hours: 0, minutes: 0, seconds: 0 }
    if (now >= START) return { phase: 'during', days: 0, hours: 0, minutes: 0, seconds: 0 }

    const total = Math.floor((START - now) / 1000)

    return {
        phase: 'before',
        days: Math.floor(total / 86400),
        hours: Math.floor((total % 86400) / 3600),
        minutes: Math.floor((total % 3600) / 60),
        seconds: total % 60
    }
}

const pad = (value: number): string => String(value).padStart(2, '0')

const Countdown: FC = (): ReactNode => {
    const [remaining, setRemaining] = useState<Remaining | null>(null)

    useEffect(() => {
        const tick = () => setRemaining(computeRemaining(Date.now()))

        tick()
        const timer = window.setInterval(tick, 1000)

        return () => window.clearInterval(timer)
    }, [])

    if (remaining && remaining.phase !== 'before') {
        return (
            <div className='flex max-w-md items-center gap-4 rounded-3xl bg-background/10 p-6 ring-1 ring-background/15'>
                <span className='grid size-12 shrink-0 place-items-center rounded-full bg-festive-saffron text-foreground'>
                    <PartyPopper className='size-5' aria-hidden='true' />
                </span>

                <p className='font-heading text-2xl leading-snug'>
                    {remaining.phase === 'during'
                        ? "C’est maintenant ! La fête bat son plein."
                        : "La fête est finie… Merci d’en avoir fait un moment inoubliable !"}
                </p>
            </div>
        )
    }

    return (
        <div role='timer' aria-live='off' aria-label='Temps restant avant la fête'>
            <dl className='grid grid-cols-4 gap-2 sm:gap-3'>
                {UNITS.map(unit => {
                    const value = remaining ? remaining[unit.key] : null

                    return (
                        <div
                            key={unit.key}
                            className='flex flex-col-reverse items-center rounded-2xl bg-background/10 px-1 py-4 text-center ring-1 ring-background/15 sm:min-w-24 sm:px-4 sm:py-5'
                        >
                            <dt className='mt-2 text-[0.65rem] font-semibold uppercase tracking-[0.06em] text-background/70 sm:text-xs sm:tracking-[0.18em]'>
                                {value === 1 ? unit.singular : unit.plural}
                            </dt>

                            <dd className='font-heading text-3xl leading-none tabular-nums min-[380px]:text-4xl sm:text-5xl'>
                                {value === null ? '--' : pad(value)}
                            </dd>
                        </div>
                    )
                })}
            </dl>
        </div>
    )
}

export default Countdown
